// React hook that resolves and holds tenant context
import { useState, useEffect } from 'react';
import { resolveTenant } from './tenantConfig.js';
import { getTenantById, getTenantByCustomDomain, listTenants } from '../api/tenantApi.js';

// Synthetic tenant object returned when the hostname is the super-admin portal.
// App.jsx checks `tenant.type === 'superadmin'` to render TenantManager.
const SUPERADMIN_TENANT = { type: 'superadmin', id: null };

export function useTenant() {
  const [tenant, setTenant] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const resolved = resolveTenant();

  useEffect(() => {
    async function load() {
      setLoading(true);
      setError(null);

      // Super-admin portal — return synthetic tenant so App.jsx can route to TenantManager
      if (resolved.isSuperAdmin) {
        setTenant(SUPERADMIN_TENANT);
        setLoading(false);
        return;
      }

      // GRC auditor portal — standalone app, no registry lookup needed
      if (resolved.tenantSlug === 'grc') {
        setTenant({ type: 'grc', id: null });
        setLoading(false);
        return;
      }

      // Universal login page — username → tenant routing, no registry lookup needed
      if (resolved.tenantSlug === 'login') {
        setTenant({ type: 'login', id: null });
        setLoading(false);
        return;
      }

      // No valid subdomain
      if (!resolved.isValid) {
        setError('no-subdomain');
        setLoading(false);
        return;
      }

      // Custom domain — distributor has pointed their own domain at this SWA.
      // Look up the tenant by the customDomain field in the registry.
      if (resolved.isCustomDomain) {
        try {
          const found = await getTenantByCustomDomain(resolved.hostname);
          if (!found) {
            setError('not-found');
          } else if (found.status === 'suspended') {
            setError('suspended');
          } else {
            setTenant(found);
          }
        } catch (e) {
          console.error('[useTenant] custom domain lookup failed:', e);
          setError('load-failed');
        }
        setLoading(false);
        return;
      }

      // Retry up to 3 times for transient network/RLS failures.
      // Only thrown errors are retried — 'not-found' and 'suspended' return a value and exit immediately.
      const id = resolved.isDevFallback ? '__dev__' : resolved.tenantSlug;
      let lastError = null;
      for (let attempt = 1; attempt <= 3; attempt++) {
        try {
          let found = null;

          // Env portals (mydev/mytest with no ?tenant=): resolve to the env's
          // real active tenant first. The synthetic __dev__ placeholder has no
          // real users, so logging into it returns "Invalid credentials".
          // Prefer the first active non-__dev__ tenant; only fall back to the
          // __dev__ placeholder when no real tenant exists.
          if (resolved.isEnvPortal) {
            const allTenants = await listTenants({ all: true });
            found =
              allTenants.find(
                (t) => t.status === 'active' && t.id !== '__dev__' && t.slug !== '__dev__'
              ) || null;
          }

          if (!found) {
            found = await getTenantById(id);
          }

          // Dev/test fallback: if __dev__ doesn't exist, pick the first active tenant
          // from the registry so the user sees a login screen instead of NoTenant.
          if (!found && resolved.isDevFallback) {
            const allTenants = await listTenants({ all: true });
            found = allTenants.find((t) => t.status === 'active') || null;
          }

          if (!found) {
            setError('not-found');
          } else if (found.status === 'suspended') {
            setError('suspended');
          } else {
            setTenant(found);
          }
          setLoading(false);
          return;
        } catch (e) {
          lastError = e;
          if (attempt < 3) {
            await new Promise((r) => setTimeout(r, 1000 * attempt)); // 1 s, then 2 s
          }
        }
      }

      console.error('[useTenant] failed to resolve tenant after retries:', lastError);
      setError('load-failed');
      setLoading(false);
    }
    load();
  }, [
    resolved.tenantSlug,
    resolved.isSuperAdmin,
    resolved.isCustomDomain,
    resolved.hostname,
    resolved.isDevFallback,
    resolved.isEnvPortal,
    resolved.isValid,
  ]);

  // Per-tenant diagnostics flag (issue #732). Lives inside the tenant record
  // in the registry JSON. Treat absent / expired config as disabled.
  const diagnostics = tenant?.diagnostics || null;
  const diagnosticsEnabled = !!(
    diagnostics &&
    diagnostics.enabled &&
    diagnostics.expiresAt &&
    Date.parse(diagnostics.expiresAt) > Date.now()
  );

  return {
    tenant,
    tenantId: tenant?.id || null,
    tenantSlug: resolved.tenantSlug,
    isSuperAdmin: resolved.isSuperAdmin,
    isDevFallback: resolved.isDevFallback || false,
    diagnostics,
    diagnosticsEnabled,
    loading,
    error,
  };
}
